// frontend/src/pages/master/DashboardPage.js
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  Button, Chip, CircularProgress, Alert, Grid, Card, CardContent, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl,
  InputLabel, Select, MenuItem, IconButton, AppBar, Toolbar, Avatar, Menu,
  Collapse, TableContainer, Stack,
} from '@mui/material';
import {
  Logout as LogoutIcon,
  Menu as MenuIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { useMediaQuery, useTheme } from '@mui/material';

const MasterDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [stats, setStats] = useState({});
  const [companies, setCompanies] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [limitsDialog, setLimitsDialog] = useState(false);
  const [limitsForm, setLimitsForm] = useState({});
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [adminName, setAdminName] = useState('Master Admin');
  
  const navigate = useNavigate();
  const API = process.env.REACT_APP_API_URL?.replace('/v1', '') || 'http://localhost:8000';
  const token = localStorage.getItem('master_token');

  // ✅ Load admin name from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('master_admin_name');
    if (stored) setAdminName(stored);
  }, []);

  const fetchAll = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, compRes, pendRes] = await Promise.all([
        axios.get(`${API}/master/stats`, { headers }),
        axios.get(`${API}/master/companies?per_page=10`, { headers }),
        axios.get(`${API}/master/subscriptions/pending?per_page=10`, { headers }), // Increased per_page
      ]);
      setStats(statsRes.data);
      setCompanies(compRes.data.data);
      setPending(pendRes.data.data);
    } catch (err) {
      if (err.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/master/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('master_token');
      localStorage.removeItem('master_admin_name');
      navigate('/master/login');
    }
  };

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleAction = async (subId, action, notes = '') => {
    setActionLoading(subId);
    try {
      await axios.post(
        `${API}/master/subscriptions/${subId}/action`,
        { action, admin_notes: notes },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      await fetchAll();
    } catch (err) {
      alert('Action failed: ' + err.response?.data?.error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdateLimits = async () => {
    try {
      await axios.put(
        `${API}/master/companies/${selectedCompany.id}/limits`,
        limitsForm,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setLimitsDialog(false);
      await fetchAll();
    } catch (err) {
      alert('Update failed: ' + err.response?.data?.error);
    }
  };

  // ✅ Helper: Check if package is Freelancer (case-insensitive)
  const isFreelancer = (pkg) => pkg?.name?.toLowerCase().trim() === 'freelancer';

  // ✅ Helper: Get status chip color
  const getStatusChip = (status) => {
    const config = {
      pending: { color: 'warning', label: '⏳ Pending' },
      approved: { color: 'success', label: '✅ Approved' },
      rejected: { color: 'error', label: '❌ Rejected' },
      cancelled: { color: 'default', label: '⚪ Cancelled' },
    };
    const cfg = config[status] || config.pending;
    return <Chip label={cfg.label} color={cfg.color} size="small" variant="outlined" />;
  };

  // ✅ Mobile Card Component for Companies
  const CompanyCard = ({ comp }) => {
    const [expanded, setExpanded] = useState(false);
    
    return (
      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }} variant="outlined">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography fontWeight="bold">{comp.company_name}</Typography>
            <Typography variant="body2" color="text.secondary">{comp.email}</Typography>
          </Box>
          <IconButton size="small" onClick={() => setExpanded(!expanded)}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        
        <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label={comp.active_subscription?.package_name || 'Free'}
            size="small"
            variant="outlined"
          />
          <Chip
            label={comp.active_subscription?.approval_status || 'No Subscription'}
            color={
              comp.active_subscription?.approval_status === 'approved' ? 'success' :
              comp.active_subscription?.approval_status === 'pending' ? 'warning' : 'default'
            }
            size="small"
          />
        </Box>

        <Collapse in={expanded}>
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Storage:</strong> {comp.storage_used_mb}MB / {comp.storage_limit_mb}MB
            </Typography>
            <Box sx={{ width: '100%', bgcolor: 'grey.200', borderRadius: 1, height: 8, mb: 1 }}>
              <Box 
                sx={{ 
                  height: '100%', 
                  bgcolor: comp.storage_percent > 90 ? 'error.main' : 'primary.main',
                  borderRadius: 1,
                  width: `${Math.min(comp.storage_percent, 100)}%`
                }} 
              />
            </Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Expires:</strong> {comp.active_subscription?.expires_at 
                ? new Date(comp.active_subscription.expires_at).toLocaleDateString() 
                : '-'}
            </Typography>
            <Button 
              size="small" 
              variant="contained"
              fullWidth
              onClick={() => {
                setSelectedCompany(comp);
                setLimitsForm({
                  max_users: comp.active_subscription?.package?.max_users || 3,
                  max_projects: comp.active_subscription?.package?.max_projects || 5,
                  storage_limit_mb: comp.active_subscription?.package?.storage_limit || 1024,
                });
                setLimitsDialog(true);
              }}
            >
              ⚙️ Manage Limits
            </Button>
          </Box>
        </Collapse>
      </Paper>
    );
  };

  // ✅ Mobile Card Component for Pending Requests
  const PendingCard = ({ sub }) => {
    const isFreelancerPkg = isFreelancer(sub.package);
    const statusConfig = {
      pending: { color: 'warning', label: '⏳ Pending', border: 'warning.main' },
      approved: { color: 'success', label: '✅ Approved', border: 'success.main' },
      rejected: { color: 'error', label: '❌ Rejected', border: 'error.main' },
      cancelled: { color: 'default', label: '⚪ Cancelled', border: 'grey.400' },
    };
    const status = statusConfig[sub.approval_status] || statusConfig.pending;

    return (
      <Paper 
        sx={{ 
          p: 2, 
          mb: 2, 
          borderRadius: 2, 
          borderLeft: `4px solid ${status.border}`
        }} 
        variant="outlined"
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography fontWeight="bold">{sub.company?.company_name}</Typography>
            <Typography variant="body2" color="text.secondary">{sub.company?.email}</Typography>
          </Box>
          {getStatusChip(sub.approval_status)}
        </Box>
        
        <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {sub.package?.name}
                {isFreelancerPkg && <span>🎁</span>}
              </Box>
            } 
            size="small" 
            color={isFreelancerPkg ? 'info' : 'primary'} 
            variant="outlined" 
          />
          <Chip 
            label={sub.package?.price === 0 ? 'Free' : `$${sub.package?.price}`} 
            size="small" 
            variant="outlined" 
          />
        </Box>
        
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Requested: {new Date(sub.created_at).toLocaleDateString()}
          {isFreelancerPkg && sub.approval_status === 'approved' && (
            <Box component="span" sx={{ ml: 1, color: 'success.main', fontWeight: 500 }}>
              • Auto-approved ✓
            </Box>
          )}
        </Typography>
        
        {sub.approval_status === 'pending' && !isFreelancerPkg && (
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button 
              size="small" 
              color="success" 
              variant="contained"
              fullWidth
              onClick={() => handleAction(sub.id, 'approve')}
              disabled={actionLoading === sub.id}
            >
              ✅ Approve
            </Button>
            <Button 
              size="small" 
              color="error" 
              variant="outlined"
              fullWidth
              onClick={() => handleAction(sub.id, 'reject')}
              disabled={actionLoading === sub.id}
            >
              ❌ Reject
            </Button>
          </Box>
        )}
        
        {sub.approval_status !== 'pending' && (
          <Alert 
            severity={sub.approval_status === 'approved' ? 'success' : 'error'} 
            size="small" 
            sx={{ mt: 2, fontSize: '0.75rem', py: 0.5 }}
          >
            {sub.approval_status === 'approved' ? '✓ Approved' : '✗ Rejected'}
            {sub.admin_notes && `: ${sub.admin_notes}`}
          </Alert>
        )}
      </Paper>
    );
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
      <CircularProgress />
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* AppBar with Logout */}
      <AppBar position="sticky" color="default" elevation={1} sx={{ mb: 2 }}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Typography variant="h6" fontWeight="bold">
            🎛️ Master Admin
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
              {adminName.charAt(0)}
            </Avatar>
            <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
              {adminName}
            </Typography>
            <IconButton onClick={handleMenuOpen} size="small">
              <MenuIcon />
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
              <MenuItem onClick={handleLogout}>
                <LogoutIcon sx={{ mr: 1 }} fontSize="small" /> Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ p: 2 }}>
                <Typography color="text.secondary" variant="caption">🏢 Companies</Typography>
                <Typography variant="h5" fontWeight="bold">{stats.total_companies || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ p: 2 }}>
                <Typography color="text.secondary" variant="caption">⏳ Pending</Typography>
                <Typography variant="h5" fontWeight="bold" color="warning.main">{stats.pending_requests || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ p: 2 }}>
                <Typography color="text.secondary" variant="caption">💰 Revenue</Typography>
                <Typography variant="h5" fontWeight="bold" color="success.main">${stats.total_revenue || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ p: 2 }}>
                <Typography color="text.secondary" variant="caption">💾 Storage</Typography>
                <Typography variant="h5" fontWeight="bold">{stats.storage_used_gb || 0} GB</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Pending Requests Section */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            ⏳ Package Requests (Pending Approval)
          </Typography>
          
          {isMobile ? (
            <Box>
              {pending.length === 0 ? (
                <Typography color="text.secondary" align="center" py={3}>
                  No pending requests
                </Typography>
              ) : (
                pending.map(sub => (
                  <PendingCard key={sub.id} sub={sub} />
                ))
              )}
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Company</TableCell>
                    <TableCell>Package</TableCell>
                    <TableCell>Price</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Requested</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pending.map(sub => {
                    const isFreelancerPkg = isFreelancer(sub.package);
                    return (
                      <TableRow key={sub.id} hover sx={{ 
                        bgcolor: isFreelancerPkg && sub.approval_status === 'approved' ? 'success.lighter' : 'inherit'
                      }}>
                        <TableCell>{sub.company?.company_name}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {sub.package?.name}
                            {isFreelancerPkg && (
                              <Chip label="🎁 Free" size="small" color="info" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>{sub.package?.price === 0 ? 'Free' : `$${sub.package?.price}`}</TableCell>
                        <TableCell>{getStatusChip(sub.approval_status)}</TableCell>
                        <TableCell>{new Date(sub.created_at).toLocaleDateString()}</TableCell>
                        <TableCell align="right">
                          {sub.approval_status === 'pending' && !isFreelancerPkg ? (
                            <>
                              <Button 
                                size="small" 
                                color="success" 
                                variant="contained"
                                onClick={() => handleAction(sub.id, 'approve')}
                                disabled={actionLoading === sub.id}
                                sx={{ mr: 1 }}
                              >
                                ✅
                              </Button>
                              <Button 
                                size="small" 
                                color="error" 
                                variant="outlined"
                                onClick={() => handleAction(sub.id, 'reject')}
                                disabled={actionLoading === sub.id}
                              >
                                ❌
                              </Button>
                            </>
                          ) : (
                            <Chip 
                              label={sub.approval_status === 'approved' ? 'Done' : sub.approval_status} 
                              size="small" 
                              color={sub.approval_status === 'approved' ? 'success' : 'default'}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {pending.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                        No pending requests
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* Companies Table */}
        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            🏢 Registered Companies
          </Typography>
          
          {isMobile ? (
            <Box>
              {companies.length === 0 ? (
                <Typography color="text.secondary" align="center" py={3}>
                  No registered companies found
                </Typography>
              ) : (
                companies.map(comp => <CompanyCard key={comp.id} comp={comp} />)
              )}
            </Box>
          ) : (
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Company Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Package</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Storage</TableCell>
                    <TableCell>Expires</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {companies.map(comp => (
                    <TableRow key={comp.id} hover>
                      <TableCell>
                        <Typography fontWeight={500}>{comp.company_name}</Typography>
                      </TableCell>
                      <TableCell>{comp.email}</TableCell>
                      <TableCell>{comp.active_subscription?.package_name || 'Free'}</TableCell>
                      <TableCell>
                        <Chip 
                          label={comp.active_subscription?.approval_status || 'No Subscription'}
                          color={
                            comp.active_subscription?.approval_status === 'approved' ? 'success' :
                            comp.active_subscription?.approval_status === 'pending' ? 'warning' : 'default'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 60 }}>
                            <Box 
                              component="progress" 
                              value={comp.storage_percent || 0} 
                              max={100}
                              sx={{ width: '100%', height: 6 }}
                            />
                          </Box>
                          <Typography variant="caption">
                            {comp.storage_used_mb}MB
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {comp.active_subscription?.expires_at 
                          ? new Date(comp.active_subscription.expires_at).toLocaleDateString() 
                          : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <Button 
                          size="small" 
                          variant="outlined"
                          onClick={() => {
                            setSelectedCompany(comp);
                            setLimitsForm({
                              max_users: comp.active_subscription?.package?.max_users || 3,
                              max_projects: comp.active_subscription?.package?.max_projects || 5,
                              storage_limit_mb: comp.active_subscription?.package?.storage_limit || 1024,
                            });
                            setLimitsDialog(true);
                          }}
                        >
                          ⚙️
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {companies.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                        No registered companies found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      {/* Limits Dialog */}
      <Dialog open={limitsDialog} onClose={() => setLimitsDialog(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Update Limits - {selectedCompany?.company_name}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Max Users"
                type="number"
                fullWidth
                value={limitsForm.max_users || ''}
                onChange={(e) => setLimitsForm({ ...limitsForm, max_users: parseInt(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Max Projects"
                type="number"
                fullWidth
                value={limitsForm.max_projects || ''}
                onChange={(e) => setLimitsForm({ ...limitsForm, max_projects: parseInt(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Storage Limit (MB)"
                type="number"
                fullWidth
                value={limitsForm.storage_limit_mb || ''}
                onChange={(e) => setLimitsForm({ ...limitsForm, storage_limit_mb: parseInt(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Admin Notes"
                multiline
                rows={2}
                fullWidth
                placeholder="Optional notes for this update"
                value={limitsForm.admin_notes || ''}
                onChange={(e) => setLimitsForm({ ...limitsForm, admin_notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setLimitsDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateLimits}>Update Limits</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MasterDashboard;